import { apiRequest } from '@/utils/api-request';

export interface CodeSourceProvenance {
  org_id: string;
  workflow_id: string;
  workflow_version_id: string;
  workflow_version_number: number;
  run_id: string;
  node_id: string;
  node_display_name: string;
  node_type: string;
  repo_url: string;
  repo: string;
  script_path: string;
  entrypoint: string;
  pinned_sha: string;
  resolved_execution_sha: string;
  manifest_path: string;
  manifest_name: string;
  matches_execution_metadata: boolean;
  repository_recorded_at_execution: boolean;
  repository_provenance_note?: string | null;
  permalink?: string | null;
}

export interface CodeSourceFile {
  path: string;
  relative_path: string;
  size_bytes: number;
  content: string;
  language: string;
  permalink?: string | null;
}

export interface CodeSourceResponse {
  provenance: CodeSourceProvenance;
  files: CodeSourceFile[];
  limits: {
    max_files: number;
    max_file_bytes: number;
    max_total_bytes: number;
    total_bytes: number;
  };
}

interface SourceErrorPayload {
  detail?: { code?: string; message?: string };
}

export class CodeSourceError extends Error {
  constructor(
    message: string,
    readonly code = 'source_unavailable',
  ) {
    super(message);
  }
}

export async function getCodeSource(
  orgId: string,
  workflowId: string,
  runId: string,
  nodeId: string,
  signal?: AbortSignal,
): Promise<CodeSourceResponse> {
  const response = await apiRequest<CodeSourceResponse>({
    url: `/api/v1/orgs/${encodeURIComponent(orgId)}/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(runId)}/nodes/${encodeURIComponent(nodeId)}/source`,
    target: 'workflow',
    method: 'GET',
    signal,
    suppressSentryStatuses: [400, 404, 409],
  });
  if (response.detail) {
    const payload = response.data as SourceErrorPayload | undefined;
    const detail = payload?.detail;
    throw new CodeSourceError(
      detail?.message ?? response.detail,
      detail?.code ?? 'source_unavailable',
    );
  }
  return (response.data ?? response) as CodeSourceResponse;
}
